import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, ChevronDown, LogOut } from 'lucide-react';
import { api } from '../../lib/api';
import {
    titleForPath,
    ROLE_BADGE,
    ROLES,
    initialsFromDisplayName,
} from '../../lib/routeConfig';

const POLL_INTERVAL_MS = 60_000;

// Phase W3.A4 — exporté pour être utilisé sur la page Commandes (ex-TopBar).
export function WebhookStatus() {
    const [healthy, setHealthy] = React.useState(null); // null=loading, bool=known
    const timerRef = React.useRef(null);

    const load = React.useCallback(async () => {
        try {
            const data = await api.webhookHealth();
            setHealthy(Boolean(data?.healthy));
        } catch (_) {
            setHealthy(false);
        }
    }, []);

    React.useEffect(() => {
        load();
        timerRef.current = setInterval(load, POLL_INTERVAL_MS);
        return () => clearInterval(timerRef.current);
    }, [load]);

    const isHealthy = healthy === true;
    const dotColor = isHealthy ? '#10B981' : '#EF4444';
    const textColor = isHealthy ? '#10B981' : '#EF4444';
    const label = isHealthy ? 'Webhook actif' : 'Webhook inactif';

    return (
        <div
            data-testid="webhook-status"
            data-healthy={String(isHealthy)}
            className="hidden md:inline-flex items-center gap-2 h-8 px-3 rounded-full text-xs font-medium"
            style={{ backgroundColor: `${dotColor}1A`, color: textColor }}
        >
            <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ backgroundColor: dotColor }}
            />
            {label}
        </div>
    );
}

function UserMenu({ user, onLogout }) {
    const [open, setOpen] = React.useState(false);
    const rootRef = React.useRef(null);

    React.useEffect(() => {
        function onDocClick(e) {
            if (!rootRef.current) return;
            if (!rootRef.current.contains(e.target)) setOpen(false);
        }
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    const initials = initialsFromDisplayName(user?.display_name);
    const badge = ROLE_BADGE[user?.role];

    return (
        <div ref={rootRef} className="relative">
            <button
                type="button"
                data-testid="user-menu-trigger"
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-2 h-9 pl-1 pr-2 rounded-full hover:bg-[#F5F6FA]"
                style={{ transitionProperty: 'background-color' }}
            >
                <span
                    data-testid="user-avatar"
                    className="w-8 h-8 rounded-full bg-[#4B3FD8] text-white text-xs font-semibold inline-flex items-center justify-center"
                >
                    {initials}
                </span>
                <span
                    data-testid="user-display-name"
                    className="text-[#1A1D2E] text-sm font-medium hidden sm:inline"
                >
                    {user?.display_name}
                </span>
                {badge && (
                    <span
                        data-testid="user-role-badge"
                        data-role={user?.role}
                        className="hidden md:inline-flex text-[10px] font-bold uppercase tracking-wider rounded-md px-2 py-0.5"
                        style={{
                            backgroundColor: `${badge.color}26`,
                            color: badge.color,
                        }}
                    >
                        {badge.label}
                    </span>
                )}
                <ChevronDown size={14} strokeWidth={1.75} className="text-[#6B7280]" />
            </button>

            {open && (
                <div
                    data-testid="user-menu"
                    role="menu"
                    className="absolute right-0 mt-2 w-56 bg-white border border-[#E5E7EB] rounded-lg shadow-lg py-1 z-50 animate-fade-in-up"
                >
                    <div className="px-3 py-2 border-b border-[#E5E7EB]">
                        <div className="text-sm font-medium text-[#1A1D2E]">
                            {user?.display_name}
                        </div>
                        <div className="text-xs text-[#6B7280] truncate">{user?.email}</div>
                    </div>
                    <button
                        type="button"
                        data-testid="logout-button"
                        onClick={() => {
                            setOpen(false);
                            onLogout();
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-[#1A1D2E] hover:bg-[#F5F6FA]"
                        style={{ transitionProperty: 'background-color' }}
                    >
                        <LogOut size={16} strokeWidth={1.75} />
                        Se déconnecter
                    </button>
                </div>
            )}
        </div>
    );
}

export default function TopBar({ user }) {
    const location = useLocation();
    const navigate = useNavigate();
    const title = titleForPath(location.pathname) || '';

    const handleLogout = async () => {
        try {
            await api.logout();
        } catch (_) {
            // ignore
        }
        navigate('/login', { replace: true });
    };

    const isAgent = user?.role === ROLES.CC_AGENT;

    return (
        <header
            data-testid="topbar"
            className="h-14 bg-white border-b border-[#E5E7EB] flex items-center px-6 gap-4 sticky top-0 z-40"
        >
            <h1
                data-testid="topbar-title"
                className="text-[18px] font-bold text-[#1A1D2E] shrink-0"
            >
                {title}
            </h1>

            {/* Right-aligned actions — pushed to the right via the flex spacer. */}
            <div className="flex-1" />

            <div className="flex items-center gap-3 shrink-0">
                {/* Phase W3.A4 — le badge Webhook (Shopify/commandes) n'est
                    plus affiché dans le header global. Il est désormais
                    rendu UNIQUEMENT sur la page Commandes. */}
                <button
                    type="button"
                    data-testid="topbar-notifications"
                    className="w-9 h-9 rounded-lg text-[#6B7280] hover:bg-[#F5F6FA] inline-flex items-center justify-center"
                    aria-label="Notifications"
                    style={{ transitionProperty: 'background-color' }}
                >
                    <Bell size={20} strokeWidth={1.75} />
                </button>
                <UserMenu user={user} onLogout={handleLogout} />
            </div>
        </header>
    );
}
